"use client";

import { useMemo, useState } from "react";

import StarRating from "@/components/ui/StarRating/StarRating";

import styles from "./BaseRating.module.css";

interface Props {
  baseId: string;
  avg: number | null;
  count: number;
}

type ActivePanel = "info" | "message" | null;

type RateResponse = {
  ok?: boolean;
  error?: string;
  details?: string;
  duplicate?: boolean;
  rating?: number | null;
  rating_count?: number;
};

export default function BaseRatingInteractive({ baseId, avg, count }: Props) {
  const [hover, setHover] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [currentAvg, setCurrentAvg] = useState<number | null>(avg);
  const [currentCount, setCurrentCount] = useState<number>(count);

  const hasRealRating =
    typeof currentAvg === "number" &&
    Number.isFinite(currentAvg) &&
    currentAvg > 0 &&
    currentCount > 0;

  const previewLabel = useMemo(() => {
    if (hover !== null) {
      return `${hover.toFixed(1)} / 5 preview`;
    }

    if (hasRealRating && currentAvg !== null) {
      return `${currentAvg.toFixed(1)} / 5`;
    }

    return "No ratings yet";
  }, [hover, hasRealRating, currentAvg]);

  function toggleInfo() {
    setMessage(null);
    setActivePanel((state) => (state === "info" ? null : "info"));
  }

  async function submitRating(value: number) {
    if (loading || submitted) return;

    setLoading(true);
    setMessage(null);
    setActivePanel(null);

    try {
      const res = await fetch("/api/rate-base", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ baseId, rating: value }),
      });

      const data = (await res.json().catch(() => null)) as RateResponse | null;

      if (!res.ok && res.status !== 409) {
        setMessage(data?.details || data?.error || "Something went wrong");
        setActivePanel("message");
        return;
      }

      const nextAvg =
        typeof data?.rating === "number" && Number.isFinite(data.rating)
          ? data.rating
          : null;

      const nextCount =
        typeof data?.rating_count === "number" && Number.isFinite(data.rating_count)
          ? data.rating_count
          : currentCount;

      setCurrentAvg(nextAvg);
      setCurrentCount(nextCount);
      setSubmitted(true);
      setHover(null);

      setMessage(
        res.status === 409 || data?.duplicate
          ? "You already rated ⭐"
          : "Thanks for rating! 🔥"
      );
      setActivePanel("message");
    } catch {
      setMessage("Network error");
      setActivePanel("message");
    } finally {
      setLoading(false);
    }
  }

  const displayValue = hasRealRating ? currentAvg ?? 0 : 0;
  const showInfo = activePanel === "info";
  const showMessage = activePanel === "message" && !!message;

  return (
    <div className={styles.ratingBox}>
      <div className={styles.row}>
        <div className={styles.starArea} onMouseLeave={() => setHover(null)}>
          <div className={styles.starShell}>
            <StarRating value={displayValue} size={18} showValue={false} />

            <div className={styles.interactiveLayer}>
              <div className={styles.buttons}>
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-label={`Rate ${value} stars`}
                    aria-disabled={submitted || loading}
                    disabled={submitted || loading}
                    onMouseEnter={() => setHover(value)}
                    onFocus={() => setHover(value)}
                    onClick={() => submitRating(value)}
                  />
                ))}
              </div>
            </div>
          </div>

          <button
            type="button"
            className={styles.infoBtn}
            onClick={toggleInfo}
            aria-label="Rating info"
            aria-expanded={showInfo}
          >
            i
          </button>

          <div className={`${styles.meta} ${showInfo ? styles.metaOpen : ""}`}>
            <div className={styles.metaValue}>{previewLabel}</div>

            {hasRealRating ? (
              <div className={styles.metaCount}>{currentCount} votes</div>
            ) : (
              <div className={styles.metaCount}>Be the first to rate</div>
            )}
          </div>
        </div>

        {hasRealRating ? (
          <div className={styles.summary} aria-live="polite">
            <strong>{displayValue.toFixed(1)}</strong>
            <span className={styles.summaryDivider}>·</span>
            <span>{currentCount} votes</span>
          </div>
        ) : null}
      </div>

      {showMessage ? (
        <p className={styles.message} role="status" aria-live="polite" aria-atomic="true">
          {message}
        </p>
      ) : null}
    </div>
  );
}
